import { useQueryClient } from "@tanstack/react-query"
import { Link, useRouter } from "@tanstack/react-router"
import { parseISO } from "date-fns"
import { Button } from "design-system/atoms/Button"
import { Dialog, DialogContent, DialogTrigger } from "design-system/atoms/Dialog"
import { Popover, PopoverContent, PopoverTrigger } from "design-system/atoms/Popover"
import { Separator } from "design-system/atoms/Separator"
import { Tooltip, TooltipContent, TooltipTrigger } from "design-system/atoms/Tooltip"
import { DropdownMenuItem } from "design-system/molecules/DropdownMenu"
import { FlagsBadge } from "design-system/molecules/FlagsBadge"
import { Chat } from "design-system/organisms/chat/Chat"
import { Message } from "design-system/organisms/chat/ChatMessage"
import { MarkdownRenderer } from "design-system/organisms/chat/MarkdownRenderer"
import { type MessageInputMenuControl } from "design-system/organisms/chat/MessageInput"
import { cn } from "design-system/utils"
import { formatDate, SECONDS_TO_MILLIS } from "lopermut"
import { ChevronDown, ChevronRight, ExternalLink, Info, NotebookPen } from "lucide-react"
import { useNewSupportFlow } from "modules/auth/hooks/useNewSupportFlow"
import { getExperimentBadgeClassName } from "modules/backoffice/lib/experiments/getExperimentBadgeClassName"
import { useConversationAmphoraOriginQuery } from "modules/conversations/hooks/useConversationAmphoraOriginQuery"
import { useConversationQuery } from "modules/conversations/hooks/useConversationQuery"
import { useConversationsQuery } from "modules/conversations/hooks/useConversationsQuery"
import { useConversationTimeline } from "modules/conversations/hooks/useConversationTimeline"
import { useExpandedConversationId } from "modules/conversations/hooks/useExpandedConversationId"
import { useUploadConversationMessageFilesMutation } from "modules/conversations/hooks/useUploadConversationMessageFilesMutation"
import { useUploadConversationMessageImagesMutation } from "modules/conversations/hooks/useUploadConversationMessageImagesMutation"
import { getConversationTitle } from "modules/conversations/lib/getConversationTitle"
import { KindMap, RoleMap } from "modules/conversations/lib/maps"
import { useIsAmphoraCompany } from "modules/customers/hooks/useIsAmphoraCompany"
import { useCreateMessageMutation } from "modules/messages/hooks/useCreateMessageMutation"
import { useCreateTestingMessageMutation } from "modules/messages/hooks/useCreateTestingMessageMutation"
import { useRateMessageMutation } from "modules/messages/hooks/useRateMessageMutation"
import { Note } from "modules/notes/components/Note"
import { NoteTextareaInput } from "modules/notes/components/NoteTextareaInput"
import { useUpsertNoteMutation } from "modules/notes/hooks/useUpsertNoteMutation"
import { scrollToNote } from "modules/notes/lib/scrollToNote"
import { Loading } from "modules/shared/components/Loading"
import { ChannelIcon } from "modules/shared/components/Miscelanea/ChannelIcon"
import { Pulse } from "modules/shared/components/Miscelanea/Pulse"
import { useDeveloperMode } from "modules/shared/hooks/useDeveloperMode"
import { ConversationSupportTimeline } from "modules/support/components/entities/ConversationSupportTimeline"
import { useConversationSupportEntitiesQuery } from "modules/support/hooks/useConversationSupportEntitiesQuery"
import { RefObject, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { useTranslation } from "react-i18next"
import {
  ChannelEnum,
  CONVERSATION_MESSAGE_FILE_MIME_TYPES,
  CONVERSATION_MESSAGE_MAX_FILE_BYTES,
  ConversationFlowEnum,
  ConversationResponseDataSchema,
  ConversationsResponseDataSchema,
  ConversationTypeEnum,
  ConversationUploadedFileSchema,
  CustomerResponseDataSchema,
  DataResponse,
  MessageFormatEnum,
} from "schema"
import { toast } from "sonner"
import { z } from "zod"
import { ConversationStatusBadge } from "../ConversationStatusBadge"
import { ProductListAttachment } from "../ProductCard/ProductListAttachment"
import { AIControls } from "./AIControls"
import { AmphoraOriginConversation } from "./AmphoraOriginConversation"
import { AmphoraOriginMarker } from "./AmphoraOriginMarker"
import { CapabilityExecutionsGroup } from "./CapabilityExecutionsGroup"
import { ConversationInsights } from "./ConversationInsights"
import { CreateFakeConversationDialog } from "./CreateFakeConversationDialog"
import { CreateNoteButton } from "./CreateNoteButton"
import { CustomerLogMarker } from "./CustomerLogMarker"
import { DeleteNoteDialog } from "./DeleteNoteDialog"
import { DeletePastFakeConversationsDialog } from "./DeletePastFakeConversationsDialog"
import { EmptyConversation } from "./EmptyConversation"
import { EmptyState } from "./EmptyState"
import { EnhanceMessageControls } from "./EnhanceMessageControls"
import { NoteControls } from "./NoteControls"
import { SupportEntityMarkers } from "./SupportEntityMarker"

const LAST_CONVERSATION_METADATA_REFETCH_INTERVAL = 60 * SECONDS_TO_MILLIS
const ACTIVE_TIMELINE_REFETCH_INTERVAL = 3 * SECONDS_TO_MILLIS
const IDLE_TIMELINE_REFETCH_INTERVAL = 30 * SECONDS_TO_MILLIS

type Props = {
  customer: z.infer<typeof CustomerResponseDataSchema>
  conversationId?: string | null
  noteId?: string
  campaignId?: string
  onConversationClick: (conversationId?: string) => void
  onIssueClick: (issueId: string) => void
  tabsListRef: RefObject<HTMLDivElement | null>
}

type UploadedConversationFile = z.infer<typeof ConversationUploadedFileSchema> & {
  previewUrl: string
}

type ConversationCapabilityExecution = NonNullable<
  z.infer<typeof ConversationResponseDataSchema>["conversationCapabilityExecutions"]
>[number]

export function ChatHistory({
  customer,
  conversationId,
  noteId,
  campaignId,
  onConversationClick,
  onIssueClick,
  tabsListRef,
}: Props) {
  const { i18n, t } = useTranslation()
  const { developerMode } = useDeveloperMode()
  const { enabledAt: newSupportFlowEnabledAt, isEnabled: isNewSupportFlowEnabled } =
    useNewSupportFlow()
  const shouldShowNewSupportEntities = isNewSupportFlowEnabled || developerMode
  const newSupportFlowEnabledTimestamp = newSupportFlowEnabledAt
    ? new Date(newSupportFlowEnabledAt).getTime()
    : null
  const queryClient = useQueryClient()
  const [selectedMessage, setSelectedMessage] = useState<Message | null>(null)
  const { buildLocation } = useRouter()

  const { data: conversations, isPending } = useConversationsQuery({
    query: {
      filterBy: {
        customerId: customer.id,
        type: customer.isFake ? ConversationTypeEnum.Testing : ConversationTypeEnum.Live,
      },
      sortBy: { field: "createdAt", order: "asc" },
    },
  })

  // Typing phase cycling (mirrors widget behaviour)
  const LOADING_KEYS = [
    "conversations.chat.loadingThinking",
    "conversations.chat.loadingChecking",
    "conversations.chat.loadingOnIt",
  ] as const
  const [typingPhaseIndex, setTypingPhaseIndex] = useState(0)
  const typingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const scheduleNextTypingPhase = useCallback(() => {
    typingTimerRef.current = setTimeout(
      () => {
        setTypingPhaseIndex((prev) => (prev + 1) % LOADING_KEYS.length)
        scheduleNextTypingPhase()
      },
      5000 + Math.random() * 5000,
    )
  }, [LOADING_KEYS.length])

  // Custom polling
  const lastConversation = conversations?.data?.at(-1)
  const [displayGenerating, setDisplayGenerating] = useState(false)
  const generatingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const { data: conversation } = useConversationQuery(lastConversation?.id.toString() ?? "", {
    refetchInterval: LAST_CONVERSATION_METADATA_REFETCH_INTERVAL,
    enabled: !!lastConversation,
  })

  const { expandedId, expandConversation, collapseConversation } = useExpandedConversationId({
    conversationId,
    lastConversationId: lastConversation?.id,
    onConversationSelect: onConversationClick,
  })

  function onTestingConversationStarted(
    response: DataResponse<z.infer<typeof ConversationResponseDataSchema>>,
  ) {
    if (response.data.conversationFlow === ConversationFlowEnum.Outbound) {
      setDisplayGenerating(true)
    }
    expandConversation(response.data.id)
  }

  const {
    messages: rawMessages,
    conversationTickets,
    conversationQuestions,
    conversationOpportunities,
    conversationCapabilityExecutions,
    customerLogs: conversationCustomerLogs,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    isLoading: isLoadingTimeline,
  } = useConversationTimeline(expandedId, {
    refetchInterval: displayGenerating
      ? ACTIVE_TIMELINE_REFETCH_INTERVAL
      : IDLE_TIMELINE_REFETCH_INTERVAL,
    includeRaw: developerMode,
  })
  const { data: conversationSupportEntities = [] } = useConversationSupportEntitiesQuery({
    query: { conversationId: expandedId ?? "" },
    enabled: shouldShowNewSupportEntities && !!expandedId,
  })
  const isAmphoraCompany = useIsAmphoraCompany()
  const { data: amphoraOrigin } = useConversationAmphoraOriginQuery(expandedId ?? "", {
    enabled: developerMode && isAmphoraCompany && !!expandedId,
  })
  const [visibleAmphoraOrigins, setVisibleAmphoraOrigins] = useState<Record<string, boolean>>({})

  const lastMessageRole = rawMessages.at(-1)?.role
  useEffect(() => {
    if (lastMessageRole && lastMessageRole !== "User") {
      setDisplayGenerating(false)
    }
  }, [customer.id, queryClient, lastMessageRole])

  useEffect(() => {
    if (displayGenerating) {
      setTypingPhaseIndex(0)
      scheduleNextTypingPhase()
      generatingTimeoutRef.current = setTimeout(
        () => setDisplayGenerating(false),
        40 * SECONDS_TO_MILLIS,
      )
    } else {
      if (typingTimerRef.current) {
        clearTimeout(typingTimerRef.current)
        typingTimerRef.current = null
      }
      if (generatingTimeoutRef.current) {
        clearTimeout(generatingTimeoutRef.current)
        generatingTimeoutRef.current = null
      }
    }
    return () => {
      if (typingTimerRef.current) clearTimeout(typingTimerRef.current)
      if (generatingTimeoutRef.current) clearTimeout(generatingTimeoutRef.current)
    }
  }, [displayGenerating, scheduleNextTypingPhase])

  const { mutateAsync: createTestingMessage, isPending: isCreatingTestingMessage } =
    useCreateTestingMessageMutation({
      onError: () => setDisplayGenerating(false),
    })

  const { mutateAsync: createMessage } = useCreateMessageMutation({
    onError: (_error, variables) => {
      setInputs((current) =>
        current[variables.conversationId]
          ? current
          : {
              ...current,
              [variables.conversationId]: variables.content ?? "",
            },
      )
      setDisplayGenerating(false)
      toast.error(t("shared.error.description"))
    },
  })
  const { mutateAsync: uploadConversationMessageImages } =
    useUploadConversationMessageImagesMutation()
  const { mutateAsync: uploadConversationMessageFiles } =
    useUploadConversationMessageFilesMutation()

  const { mutateAsync: rateMessage } = useRateMessageMutation({
    onError: () => setDisplayGenerating(false),
    onSuccess: () => {
      toast.success(t("conversations.chat.messageRated.success"))
    },
  })

  const { mutateAsync: upsertNote } = useUpsertNoteMutation()

  // Fetch the expanded conversation individually when it's not the last one
  const isExpandedNotLast = !!expandedId && expandedId !== lastConversation?.id
  const { data: expandedConversation } = useConversationQuery(expandedId ?? "", {
    enabled: isExpandedNotLast,
  })

  const activeConversationFlags = useMemo(() => {
    const activeConversation = expandedId === conversation?.id ? conversation : expandedConversation
    return activeConversation?.conversationFlags ?? []
  }, [conversation, expandedConversation, expandedId])

  const conversationFlags = useMemo(
    () => activeConversationFlags.filter((flag) => !flag.messageId),
    [activeConversationFlags],
  )

  const flagsByMessageId = useMemo(() => {
    const result = new Map<string, typeof activeConversationFlags>()

    for (const flag of activeConversationFlags) {
      if (!flag.messageId) continue
      const messageFlags = result.get(flag.messageId) ?? []
      messageFlags.push(flag)
      result.set(flag.messageId, messageFlags)
    }

    return result
  }, [activeConversationFlags])

  const [inputs, setInputs] = useState<Record<string, string>>({})
  const [uploadedFiles, setUploadedFiles] = useState<
    Record<string, Record<string, UploadedConversationFile>>
  >({})
  const [aiToggleCooldowns, setAiToggleCooldowns] = useState<Record<string, number>>({})
  const [noteModes, setNoteModes] = useState<Record<string, boolean>>({})
  const [deletingNote, setDeletingNote] = useState<{ id: string; conversationId: string } | null>(
    null,
  )
  const [tick, setTick] = useState(0)

  const handleInputChange = (id: string, value: string) => {
    setInputs((prev) => ({ ...prev, [id]: value }))
  }

  const handleSubmit = async (
    conversation: z.infer<typeof ConversationsResponseDataSchema>[number],
    event?: { preventDefault?: () => void },
    options?: { experimental_attachments?: FileList },
  ) => {
    event?.preventDefault?.()
    const files = Array.from(options?.experimental_attachments ?? [])
    const uploadedFilesForConversation = uploadedFiles[conversation.id] ?? {}
    const selectedUploadedFiles = files.map(
      (file) => uploadedFilesForConversation[getUploadFileKey(file)],
    )
    const content = inputs[conversation.id]?.trim() ? inputs[conversation.id] : undefined

    if (
      conversation.type === ConversationTypeEnum.Live &&
      !customer.isFake &&
      getIsWhatsappWindowExpired(conversation, rawMessages) &&
      ((content?.trim().length ?? 0) > 1024 ||
        /\{\{[\s\S]*?\}\}/.test(content ?? "") ||
        files.length > 0)
    ) {
      toast.error(t("conversations.chat.oneTimeTemplateInvalid"))
      return
    }

    if (files.length && selectedUploadedFiles.some((image) => !image)) {
      toast.error(t("shared.error.description"))
      throw new Error("Missing uploaded conversation file")
    }

    const restoreSubmittedText = () => {
      setInputs((current) =>
        current[conversation.id] ? current : { ...current, [conversation.id]: content ?? "" },
      )
    }

    const clearSubmittedUploads = () => {
      if (files.length) {
        setUploadedFiles((current) => {
          const remaining = { ...current[conversation.id] }
          for (const file of files) {
            const key = getUploadFileKey(file)
            if (remaining[key] === uploadedFilesForConversation[key]) delete remaining[key]
          }
          return { ...current, [conversation.id]: remaining }
        })
      }
    }

    setInputs((current) => ({ ...current, [conversation.id]: "" }))

    if (conversation.type === "Testing") {
      await createTestingMessage({
        conversationId: conversation.id,
        content,
        uploadedImages: selectedUploadedFiles.filter(
          (file): file is UploadedConversationFile & { format: typeof MessageFormatEnum.Image } =>
            file?.format === MessageFormatEnum.Image,
        ),
      }).catch((error: unknown) => {
        restoreSubmittedText()
        throw error
      })

      clearSubmittedUploads()
      setDisplayGenerating(true)
    } else {
      await createMessage(
        {
          conversationId: conversation.id,
          content,
          uploadedFiles: selectedUploadedFiles.filter(
            (image): image is UploadedConversationFile => !!image,
          ),
        },
        { onSuccess: clearSubmittedUploads },
      )
    }
  }

  const handleFileUpload = async (
    conversation: z.infer<typeof ConversationsResponseDataSchema>[number],
    files: File[],
  ) => {
    if (
      conversation.type !== ConversationTypeEnum.Live &&
      conversation.type !== ConversationTypeEnum.Testing
    ) {
      return
    }

    if (files.some((file) => file.size > CONVERSATION_MESSAGE_MAX_FILE_BYTES)) {
      toast.error(t("conversations.chat.fileTooLarge"))
      throw new Error("Conversation file exceeds 50 MB")
    }

    try {
      const uploadFiles =
        conversation.type === ConversationTypeEnum.Testing
          ? uploadConversationMessageImages
          : uploadConversationMessageFiles

      const data = await uploadFiles({
        conversationId: conversation.id,
        files,
      })

      setUploadedFiles((current) => {
        const currentConversationFiles = current[conversation.id] ?? {}

        return {
          ...current,
          [conversation.id]: {
            ...currentConversationFiles,
            ...Object.fromEntries(
              files.flatMap((file, index) => {
                const uploadedFile = data[index]
                if (!uploadedFile) return []

                return [
                  [
                    getUploadFileKey(file),
                    {
                      ...uploadedFile,
                      previewUrl: file.type.startsWith("image/")
                        ? URL.createObjectURL(file)
                        : uploadedFile.mediaUrl,
                    },
                  ],
                ]
              }),
            ),
          },
        }
      })
    } catch (error) {
      toast.error(t("shared.error.description"))
      throw error
    }
  }

  const messages = useMemo(() => {
    const messages = rawMessages.map((message) => {
      const overrides: Partial<Message> = {}

      if (message.format === MessageFormatEnum.Unsupported) {
        overrides.transcription = t("conversations.chat.unsupportedMessage")
        overrides.content = null
      }

      return {
        ...message,
        id: message.id,
        role: RoleMap[message.role],
        content: message.content,
        mediaUrl: message.mediaUrl,
        transcription: message.transcription,
        externalDeletedAt: message.externalDeletedAt,
        format: KindMap[message.format],
        kind: message.kind,
        createdAt: new Date(message.createdAt),
        readAt: message.readAt ? new Date(message.readAt) : null,
        messageReaction: message.messageReactions?.at(0)?.emoji ?? null,
        authorName:
          message.employee?.name ??
          (message.source === "whatsapp_business_app" && message.role === "Assistant"
            ? t("conversations.chat.whatsappBusinessAppAuthor")
            : undefined),
        source: message.source,
        whatsappTemplate: message.whatsappTemplate
          ? {
              id: message.whatsappTemplate.id,
              name: message.whatsappTemplate.name,
              mediaFormat: message.whatsappTemplate.mediaFormat,
              mediaUrl: message.whatsappTemplate.mediaUrl,
            }
          : null,
        responseToMessageId: message.responseToMessageId,
        renderAttachments: () => {
          const productList = message.attachments?.find((a) => a?.type === "productList")
          if (!productList) return undefined
          return (
            <div className="mt-2 -mx-4 -mb-1">
              <ProductListAttachment products={productList.products} />
            </div>
          )
        },
        flagCounts: developerMode
          ? (() => {
              const messageFlags = flagsByMessageId.get(message.id) ?? []
              const issues = messageFlags.filter((flag) => flag.kind === "Issue").length
              const frictions = messageFlags.filter((flag) => flag.kind === "Friction").length

              return issues > 0 || frictions > 0 ? { issues, frictions } : undefined
            })()
          : undefined,
        ...overrides,
      }
    })

    const visibleCustomerLogIds = new Set<string>()
    let latestVisibleTrollingLogId: string | undefined

    const sortedCustomerLogs = [...conversationCustomerLogs].sort(
      (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    )

    for (const log of sortedCustomerLogs) {
      if (log.kind === "TrollingDetected") {
        if (latestVisibleTrollingLogId) {
          visibleCustomerLogIds.delete(latestVisibleTrollingLogId)
        }

        visibleCustomerLogIds.add(log.id)
        latestVisibleTrollingLogId = log.id
        continue
      }

      visibleCustomerLogIds.add(log.id)
      latestVisibleTrollingLogId = undefined
    }

    return messages.map((message, index) => {
      const nextMessage = messages[index + 1]
      const messageDate = message.createdAt
      const nextMessageDate = nextMessage?.createdAt

      // Find tickets created or updated between this message and the next
      const ticketCreatedBetween = conversationTickets.find((ticket) => {
        const ticketCreatedAt = new Date(ticket.createdAt)
        if (
          newSupportFlowEnabledTimestamp !== null &&
          ticketCreatedAt.getTime() >= newSupportFlowEnabledTimestamp
        ) {
          return false
        }

        return nextMessageDate
          ? ticketCreatedAt > messageDate && ticketCreatedAt < nextMessageDate
          : ticketCreatedAt > messageDate
      })

      const questionCreatedBetween = conversationQuestions.find((question) => {
        const questionCreatedAt = new Date(question.createdAt)
        if (
          newSupportFlowEnabledTimestamp !== null &&
          questionCreatedAt.getTime() >= newSupportFlowEnabledTimestamp
        ) {
          return false
        }

        return nextMessageDate
          ? questionCreatedAt > messageDate && questionCreatedAt < nextMessageDate
          : questionCreatedAt > messageDate
      })

      const questionSolvedAutomaticallyBetween = conversationQuestions.find((question) => {
        if (!question.resolvedAutomaticallyAt) return false
        if (
          newSupportFlowEnabledTimestamp !== null &&
          new Date(question.createdAt).getTime() >= newSupportFlowEnabledTimestamp
        ) {
          return false
        }

        const questionSolvedAt = new Date(question.resolvedAutomaticallyAt)
        return nextMessageDate
          ? questionSolvedAt > messageDate && questionSolvedAt < nextMessageDate
          : questionSolvedAt > messageDate
      })

      const tickets = []
      if (ticketCreatedBetween) {
        const questionLinkedToTicket = conversationQuestions.find(
          (q) => q.conversationTicketId === ticketCreatedBetween.id,
        )
        tickets.push({
          id: ticketCreatedBetween.id,
          kind: "ticket" as const,
          entityAutoResolved: Boolean(questionLinkedToTicket?.resolvedAutomaticallyAt),
        })
      }

      if (questionCreatedBetween) {
        tickets.push({
          id: questionCreatedBetween.id,
          kind: "question" as const,
          subject: questionCreatedBetween.subject ?? null,
          summary: questionCreatedBetween.summary,
          hasRaisedTicket: Boolean(questionCreatedBetween.conversationTicketId),
          entityAutoResolved: Boolean(questionCreatedBetween.resolvedAutomaticallyAt),
        })
      }

      if (questionSolvedAutomaticallyBetween) {
        tickets.push({
          id: questionSolvedAutomaticallyBetween.id,
          kind: "question" as const,
          resolvedAutomatically: true,
          subject: questionSolvedAutomaticallyBetween.subject ?? null,
          summary: questionSolvedAutomaticallyBetween.summary,
          hasRaisedTicket: Boolean(questionSolvedAutomaticallyBetween.conversationTicketId),
          entityAutoResolved: Boolean(questionSolvedAutomaticallyBetween.resolvedAutomaticallyAt),
        })
      }

      const opportunities = conversationOpportunities.filter((opportunity) => {
        const createdAt = new Date(opportunity.createdAt)
        const isInTimeRange = nextMessageDate
          ? createdAt > messageDate && createdAt < nextMessageDate
          : createdAt > messageDate
        const isQualified =
          opportunity.qualification === "Potential" || opportunity.qualification === "Qualified"

        return isInTimeRange && isQualified
      })

      const capabilityExecutions = conversationCapabilityExecutions.filter((execution) => {
        if (!execution.isVisibleInConversation) return false
        const createdAt = new Date(execution.createdAt)
        return nextMessageDate
          ? createdAt > messageDate && createdAt < nextMessageDate
          : createdAt > messageDate
      })

      const supportEntities = conversationSupportEntities.filter((item) => {
        const createdAt = new Date(item.createdAt)
        if (
          newSupportFlowEnabledTimestamp !== null &&
          createdAt.getTime() < newSupportFlowEnabledTimestamp
        ) {
          return false
        }

        return nextMessageDate
          ? createdAt >= messageDate && createdAt < nextMessageDate
          : createdAt >= messageDate
      })

      const attachedCustomerLogs = conversationCustomerLogs.filter((log) => {
        if (!visibleCustomerLogIds.has(log.id)) return false

        const logCreatedAt = new Date(log.createdAt)
        return nextMessageDate
          ? logCreatedAt > messageDate && logCreatedAt < nextMessageDate
          : logCreatedAt > messageDate
      })
      const hasSupportEntities = shouldShowNewSupportEntities && supportEntities.length > 0
      const hasTimelineMarkers = attachedCustomerLogs.length > 0 || hasSupportEntities

      return {
        ...message,
        tickets,
        opportunities,
        capabilityExecutions,
        customerLogs: undefined,
        renderTimelineMarkers: hasTimelineMarkers
          ? () => (
              <>
                {attachedCustomerLogs.map((log) => (
                  <CustomerLogMarker key={log.id} log={log} />
                ))}
                {hasSupportEntities && (
                  <SupportEntityMarkers items={supportEntities} onIssueClick={onIssueClick} />
                )}
              </>
            )
          : undefined,
      }
    })
  }, [
    rawMessages,
    conversationTickets,
    conversationQuestions,
    conversationOpportunities,
    conversationCapabilityExecutions,
    conversationSupportEntities,
    conversationCustomerLogs,
    developerMode,
    flagsByMessageId,
    newSupportFlowEnabledTimestamp,
    onIssueClick,
    shouldShowNewSupportEntities,
    t,
  ])

  const activeSelectedMessage =
    selectedMessage && messages.some((message) => message.id === selectedMessage.id)
      ? selectedMessage
      : null

  const conversationsData = useMemo(
    () =>
      conversation
        ? [...(conversations?.data.slice(0, -1) ?? []), conversation]
        : conversations?.data,
    [conversations, conversation],
  )

  const currentConversation = useMemo(() => {
    if (!expandedId) return undefined
    if (expandedId === conversation?.id) return conversation
    return expandedConversation ?? conversationsData?.find((c) => c.id === expandedId)
  }, [conversation, conversationsData, expandedConversation, expandedId])

  const [tabsListElement, setTabsListElement] = useState<HTMLDivElement | null>(null)

  const fakeConversationActions = () => {
    const element = tabsListElement

    if (conversationsData && customer.isFake && element) {
      return createPortal(
        <>
          <CreateFakeConversationDialog
            customer={customer}
            campaignId={campaignId}
            onTestingConversationStarted={onTestingConversationStarted}
          />
          <DeletePastFakeConversationsDialog
            customer={customer}
            conversations={conversationsData}
          />
        </>,
        element,
      )
    }

    return null
  }

  function getIsWhatsappWindowExpired(
    conversation: z.infer<typeof ConversationsResponseDataSchema>[number],
    messages: Array<{ role: string; createdAt: string }> | undefined,
  ) {
    if (conversation.channel !== ChannelEnum.Whatsapp) return false
    if (!messages) return false

    const userMessages = messages.filter((message) => message.role === "User")
    if (userMessages.length === 0) return true

    const lastUserMessageDate = new Date(
      Math.max(...userMessages.map((message) => new Date(message.createdAt).getTime())),
    )
    const hoursSinceLastMessage = (Date.now() - lastUserMessageDate.getTime()) / (1000 * 60 * 60)
    return hoursSinceLastMessage >= 24
  }

  function isChatDisabled(
    conversation: z.infer<typeof ConversationsResponseDataSchema>[number],
    customer: z.infer<typeof CustomerResponseDataSchema>,
    isWhatsappWindowExpired: boolean,
  ) {
    const isLive = conversation.type === ConversationTypeEnum.Live

    // Live conversations are disabled when AI is enabled for the customer,
    // or when the conversation is already closed
    if (isLive) {
      if (
        conversation.channel === ChannelEnum.Whatsapp &&
        isWhatsappWindowExpired &&
        !customer.isFake
      )
        return !conversation.aiDisabled
      return !conversation.aiDisabled || !!conversation.closedAt || isWhatsappWindowExpired
    }

    // Testing conversations are disabled when:
    // 1. Customer uses a channel, OR
    // 2. It's an outbound conversation with no messages yet, as we are sending the first message
    if (customer.useChannel || isCreatingTestingMessage) return true
    if (
      conversation.conversationFlow === ConversationFlowEnum.Outbound &&
      !conversation.hasMessages
    )
      return true

    return false
  }

  function hideEnhanceMessageControls(
    conversation: z.infer<typeof ConversationsResponseDataSchema>[number],
  ) {
    return !conversation.aiDisabled
  }

  function hideAIControls() {
    const isLive = currentConversation?.type === ConversationTypeEnum.Live
    return !isLive || customer.isFake
  }

  useEffect(() => {
    // Ref assignments happen after render; store in state to trigger re-render for portal target.
    setTabsListElement(tabsListRef.current)
  }, [tabsListRef])

  useEffect(() => {
    // Re-enables manual control button 10 seconds after resuming AI agent
    if (!expandedId) return
    const cooldown = aiToggleCooldowns[expandedId]
    if (!cooldown) return
    const now = Date.now()
    if (now >= cooldown) return
    const timeout = setTimeout(() => setTick((t) => t + 1), cooldown - now + 10)
    return () => clearTimeout(timeout)
  }, [expandedId, aiToggleCooldowns, tick])

  useEffect(() => {
    if (noteId && messages.length) {
      scrollToNote(noteId)
    }
  }, [noteId, messages])

  if (isPending) return <Loading />

  if (conversations?.data.length === 0) {
    return (
      <EmptyState
        customer={customer}
        campaignId={campaignId}
        onTestingConversationStarted={onTestingConversationStarted}
      />
    )
  }

  return (
    <div className="flex flex-col w-full h-full gap-2 grow" id="chat-wrapper">
      {fakeConversationActions()}
      <div className="gap-2 overflow-y-auto h-full flex flex-col">
        {conversationsData?.map((conversation) => {
          const isLive = conversation.type === ConversationTypeEnum.Live
          const isExpanded = conversation.id === expandedId
          const campaignName = conversation.campaign?.name
          const flags = conversation.conversationFlags ?? []
          const issueCount = flags.filter((flag) => flag.kind === "Issue").length
          const frictionCount = flags.filter((flag) => flag.kind === "Friction").length

          const isAlreadyClosed = isLive && !!conversation.closedAt
          const whatsappWindowMessages =
            conversation.id === expandedId && !isLoadingTimeline ? rawMessages : undefined
          const isWhatsappWindowExpired = getIsWhatsappWindowExpired(
            conversation,
            whatsappWindowMessages,
          )
          const chatDisabled = isChatDisabled(conversation, customer, isWhatsappWindowExpired)
          const isOneTimeTemplateMode =
            isLive &&
            !customer.isFake &&
            conversation.channel === ChannelEnum.Whatsapp &&
            isWhatsappWindowExpired
          const isReplyDisabledInHistory =
            !isOneTimeTemplateMode &&
            conversation.id !== lastConversation?.id &&
            (conversation.channel === ChannelEnum.Instagram ||
              conversation.channel === ChannelEnum.Whatsapp)

          let placeholder =
            chatDisabled || isReplyDisabledInHistory
              ? t("conversations.chat.chatPlaceholderDisabled")
              : t("conversations.chat.chatPlaceholder")
          if (isAlreadyClosed) {
            placeholder = t("conversations.chat.chatPlaceholderClosed")
          }

          if (isOneTimeTemplateMode && !isReplyDisabledInHistory) {
            placeholder = conversation.aiDisabled
              ? t("conversations.chat.oneTimeTemplatePlaceholder")
              : t("conversations.chat.chatPlaceholderPauseAgent", {
                  action: t("conversations.chat.manualControl.switchToManual"),
                })
          }

          const conversationTitle = getConversationTitle(conversation, t)
          const isConversationBeforeNewSupportFlow =
            newSupportFlowEnabledTimestamp !== null &&
            new Date(conversation.createdAt).getTime() < newSupportFlowEnabledTimestamp
          const shouldShowConversationSupport =
            developerMode || (isNewSupportFlowEnabled && !isConversationBeforeNewSupportFlow)

          const assignedExperiments =
            currentConversation?.conversationExperiments?.filter(
              (assignment) => assignment.experiment !== "production",
            ) ?? []
          const extraInfo = expandedId && conversation && (
            <div className="flex flex-col gap-6">
              {developerMode && assignedExperiments.length > 0 && (
                <div className="flex flex-col gap-4">
                  <span className="text-lg">{t("conversations.chat.experiment.title")}</span>
                  {assignedExperiments.map((assignment) => (
                    <div
                      key={assignment.domain}
                      className={cn(
                        "inline-flex w-fit max-w-full items-center rounded-md border px-2.5 py-0.5 text-xs font-semibold shadow-xs",
                        "font-mono normal-case",
                        getExperimentBadgeClassName(assignment.experiment),
                      )}
                      title={t("conversations.chat.experiment.option", {
                        domain: assignment.domain,
                        name: assignment.experiment,
                      })}
                    >
                      <span className="block max-w-[min(100%,16rem)] truncate">
                        {assignment.experiment}
                      </span>
                    </div>
                  ))}
                </div>
              )}
              {shouldShowConversationSupport && (
                <ConversationSupportTimeline
                  conversationId={expandedId}
                  createdAtFrom={newSupportFlowEnabledAt ?? undefined}
                />
              )}
              <ConversationInsights
                conversationId={expandedId}
                overallSentiment={currentConversation?.overallSentiment}
                selectedMessage={activeSelectedMessage}
                customer={customer}
                noteItems={currentConversation?.notes}
                selectedMessageFlags={
                  activeSelectedMessage
                    ? (flagsByMessageId.get(activeSelectedMessage.id) ?? [])
                    : []
                }
                conversationFlags={conversationFlags}
                showFlags={developerMode}
              />
            </div>
          )

          let formattedEvent = null
          if (conversation.event) {
            try {
              formattedEvent = JSON.stringify(conversation.event, null, 2)
            } catch {}
          }

          const isNoteMode = noteModes[conversation.id] ?? false
          const conversationAmphoraOrigin =
            developerMode && isExpanded && amphoraOrigin?.conversations.length
              ? amphoraOrigin
              : null
          const isAmphoraOriginVisible =
            !!conversationAmphoraOrigin && (visibleAmphoraOrigins[conversation.id] ?? false)
          const canUploadFiles =
            !chatDisabled &&
            !isOneTimeTemplateMode &&
            !isNoteMode &&
            !isAlreadyClosed &&
            (conversation.type === ConversationTypeEnum.Testing || isLive)

          const exitNoteMode = () => {
            setNoteModes((prev) => ({ ...prev, [conversation.id]: false }))
            setInputs((prev) => ({ ...prev, [conversation.id]: "" }))
          }

          const noteToggle = () => {
            if (isNoteMode) {
              exitNoteMode()
            } else {
              setNoteModes((prev) => ({ ...prev, [conversation.id]: true }))
            }
          }

          const customControlsLeft = (() => {
            if (hideAIControls()) return undefined

            return (
              <div className="flex flex-row gap-2">
                {isNoteMode ? (
                  <NoteControls isNoteMode={isNoteMode} onToggle={noteToggle} />
                ) : (
                  <AIControls
                    conversation={conversation}
                    aiToggleCooldowns={aiToggleCooldowns}
                    onSetAiToggleCooldowns={setAiToggleCooldowns}
                    onSetInputs={setInputs}
                    switchToManualDisabledTooltip={
                      isAlreadyClosed && !isOneTimeTemplateMode
                        ? t("conversations.chat.manualControl.concludedTooltip")
                        : undefined
                    }
                  />
                )}
              </div>
            )
          })()

          const customControlsRight = (() => {
            if (isNoteMode) {
              return (
                <CreateNoteButton
                  conversationId={conversation.id}
                  inputs={inputs}
                  onSetInputs={setInputs}
                  onExitNoteMode={exitNoteMode}
                />
              )
            }
            return undefined
          })()

          const customControlsMenu = (() => {
            const showNoteControl = !isNoteMode && !hideAIControls()
            const showEnhanceControl = !isNoteMode && !hideEnhanceMessageControls(conversation)
            const controls: MessageInputMenuControl[] = []

            if (showEnhanceControl) {
              controls.push({
                key: "enhance-message",
                menuItem: (
                  <EnhanceMessageControls
                    customer={customer}
                    conversationId={conversation.id}
                    inputs={inputs}
                    onSetInputs={setInputs}
                    mode="menuItem"
                  />
                ),
                button: (
                  <EnhanceMessageControls
                    customer={customer}
                    conversationId={conversation.id}
                    inputs={inputs}
                    onSetInputs={setInputs}
                    buttonVariant="secondary"
                    showLabel
                  />
                ),
              })
            }

            if (showNoteControl) {
              controls.push({
                key: "add-note",
                menuItem: (
                  <DropdownMenuItem onSelect={noteToggle}>
                    <NotebookPen />
                    <span>{t("conversations.chat.notes.addNote")}</span>
                  </DropdownMenuItem>
                ),
                button: (
                  <Button
                    type="button"
                    size="icon"
                    variant="secondary"
                    aria-label={t("conversations.chat.notes.addNote")}
                    title={t("conversations.chat.notes.addNote")}
                    onClick={noteToggle}
                  >
                    <NotebookPen />
                    <span className="sr-only">{t("conversations.chat.notes.addNote")}</span>
                  </Button>
                ),
              })
            }

            return controls.length ? controls : undefined
          })()

          return (
            <div
              key={conversation.id}
              className={cn(
                "flex flex-col gap-4",
                isExpanded && "flex-1 max-h-80vh h-full min-w-96",
              )}
            >
              <Button
                variant="ghost"
                className={cn(
                  "px-4 py-1.5 h-auto hover:bg-secondary",
                  isExpanded && "bg-secondary",
                )}
                aria-expanded={isExpanded}
                onClick={() => {
                  if (!isExpanded) {
                    expandConversation(conversation.id)
                  } else {
                    collapseConversation()
                  }
                }}
              >
                <div className="flex flex-row items-center justify-between gap-2 w-full h-full">
                  <div className="flex flex-col sm:flex-row items-center justify-between gap-2 w-full h-full">
                    <div className="flex flex-row items-center gap-2 w-full min-w-0">
                      {!conversation.closedAt && (
                        <Tooltip>
                          <TooltipTrigger title={t("conversations.active.title")}>
                            <Pulse
                              className={cn("fill-chart-1", customer.isFake && "fill-testing-1")}
                            />
                          </TooltipTrigger>
                          <TooltipContent side="right">
                            {t("conversations.active.explanation")}
                          </TooltipContent>
                        </Tooltip>
                      )}
                      <Link
                        to="/campaigns/$id"
                        params={{ id: conversation.campaign?.id ?? "" }}
                        target="_blank"
                        disabled={!conversation.campaign}
                        title={conversationTitle}
                        className={cn(
                          "text-foreground text-sm transition-colors hover:text-primary hover:underline truncate",
                          !campaignName && "text-muted-foreground",
                        )}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {conversationTitle}
                      </Link>
                    </div>
                    <div className="flex flex-row items-center gap-2 w-full sm:justify-end font-thin text-xs">
                      {developerMode && (issueCount > 0 || frictionCount > 0) && (
                        <FlagsBadge issuesCount={issueCount} frictionsCount={frictionCount} />
                      )}
                      <div className="flex flex-row items-center gap-4">
                        <ConversationStatusBadge
                          status={conversation.status}
                          failedReason={conversation.failedReason}
                          whatsappRoute={conversation.whatsappRoute}
                          conversationFlow={conversation.conversationFlow}
                          hasTickets={conversation.hasTickets}
                        />
                        <ChannelIcon channel={conversation.channel} withTooltip />
                        <span>{formatDate(parseISO(conversation.createdAt), "Pp")}</span>
                      </div>
                    </div>
                  </div>
                  {isExpanded ? <ChevronDown /> : <ChevronRight />}
                </div>
              </Button>
              {isExpanded && (
                <div className="flex flex-col gap-2 flex-1 min-h-0">
                  <div className="flex flex-row gap-4 flex-1 min-h-0">
                    <div
                      className="flex-1 flex flex-col gap-2 min-h-0"
                      onClick={() => setSelectedMessage(null)}
                    >
                      <div className="flex flex-row items-center gap-2 w-full justify-between">
                        <span className="text-lg">{t("conversations.chat.title")}</span>
                        <div className="flex flex-row gap-2 h-10">
                          {customer?.isFake && (
                            <Button
                              size="icon"
                              variant="outline"
                              onClick={async () => {
                                const link = buildLocation({
                                  to: "/conversations/list/$customerId",
                                  params: { customerId: customer.id },
                                  search: { conversationId: conversation.id, tab: "conversations" },
                                }).href

                                await navigator.clipboard.writeText(
                                  [window.location.origin, link].join(""),
                                )
                                toast.success(t("shared.copySuccess"))
                              }}
                            >
                              <ExternalLink />
                            </Button>
                          )}
                          <div className="lg:hidden flex w-full justify-end">
                            <Dialog>
                              <DialogContent className="p-6">{extraInfo}</DialogContent>
                              <DialogTrigger>
                                <Button variant="outline" size="sm">
                                  <Info className="size-4" />
                                  <span>{t("conversations.chat.extraInfo")}</span>
                                </Button>
                              </DialogTrigger>
                            </Dialog>
                          </div>
                          {conversation.event && formattedEvent && (
                            <Popover>
                              <PopoverTrigger>
                                <Button variant="outline" size="sm">
                                  <Info className="size-4" />
                                  <span>{t("conversations.chat.metadata")}</span>
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="flex flex-col gap-4">
                                <div className="flex flex-col">
                                  <span className="text-lg">
                                    {t("conversations.chat.metadata")}
                                  </span>
                                  <span className="text-sm text-muted-foreground">
                                    {t("conversations.chat.metadataDescription")}
                                  </span>
                                </div>
                                <MarkdownRenderer>{`\`\`\`json\n${formattedEvent}\n\`\`\``}</MarkdownRenderer>
                              </PopoverContent>
                            </Popover>
                          )}
                        </div>
                      </div>
                      {expandedId === conversation.id && isLoadingTimeline && (
                        <div className="flex justify-center py-4">
                          <Loading />
                        </div>
                      )}
                      <Chat
                        scrollHeader={
                          <>
                            {isAmphoraOriginVisible && conversationAmphoraOrigin && (
                              <AmphoraOriginConversation origin={conversationAmphoraOrigin} />
                            )}
                            {conversationAmphoraOrigin && (
                              <AmphoraOriginMarker
                                isExpanded={isAmphoraOriginVisible}
                                onToggle={() =>
                                  setVisibleAmphoraOrigins((prev) => ({
                                    ...prev,
                                    [conversation.id]: !prev[conversation.id],
                                  }))
                                }
                              />
                            )}
                            {expandedId === conversation.id && hasNextPage && (
                              <div className="flex justify-center py-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  disabled={isFetchingNextPage}
                                  onClick={() => fetchNextPage()}
                                >
                                  {isFetchingNextPage
                                    ? t("conversations.chat.loadingOlderMessages")
                                    : t("conversations.chat.loadOlderMessages")}
                                </Button>
                              </div>
                            )}
                          </>
                        }
                        messages={messages}
                        locale={i18n.language}
                        isFakeCustomer={customer.isFake}
                        input={inputs[conversation.id] || ""}
                        handleInputChange={(e) =>
                          handleInputChange(conversation.id, e.target.value)
                        }
                        handleSubmit={(e, options) => handleSubmit(conversation, e, options)}
                        allowAttachments={canUploadFiles}
                        acceptedFileTypes={
                          isLive ? CONVERSATION_MESSAGE_FILE_MIME_TYPES : undefined
                        }
                        uploadFilesLabel={isLive ? t("conversations.chat.attachFiles") : undefined}
                        onUploadFiles={(files) => handleFileUpload(conversation, files)}
                        notePlaceholder={t("conversations.chat.notes.placeholder")}
                        isGenerating={
                          conversation.id === conversations?.data.at(-1)?.id && displayGenerating
                        }
                        typingLabel={
                          conversation.id === conversations?.data.at(-1)?.id && displayGenerating
                            ? t(LOADING_KEYS[typingPhaseIndex % LOADING_KEYS.length]!)
                            : undefined
                        }
                        isClosed={isAlreadyClosed}
                        isNoteMode={noteModes[conversation.id] ?? false}
                        disabled={
                          noteModes[conversation.id]
                            ? false
                            : chatDisabled || isReplyDisabledInHistory
                        }
                        inputMaxLength={isOneTimeTemplateMode ? 1024 : undefined}
                        placeholder={placeholder}
                        interruptMessage={t("conversations.chat.interruptMessage")}
                        className="flex flex-col sm:min-w-150 min-w-auto flex-1 min-h-100 overflow-y-auto overflow-x-hidden pl-2"
                        supportCopy={{
                          questionCreated: t("conversations.chat.support.questionCreated"),
                          questionSolvedAutomatically: t(
                            "conversations.chat.support.questionSolvedAutomatically",
                          ),
                          ticketCreated: t("conversations.chat.support.ticketCreated"),
                          questionSubjectLabel: t("conversations.chat.supportQuestion.subject"),
                          questionSummaryLabel: t("conversations.chat.supportQuestion.summary"),
                        }}
                        opportunityCopy={t("conversations.chat.opportunities.detected")}
                        renderCapabilityExecutions={(executions) =>
                          executions ? (
                            <CapabilityExecutionsGroup
                              executions={executions as ConversationCapabilityExecution[]}
                            />
                          ) : null
                        }
                        renderCustomerLogMarkers={(logs) =>
                          logs.map((log) => <CustomerLogMarker key={log.id} log={log} />)
                        }
                        onRateResponse={(messageId, rating) => {
                          rateMessage({
                            messageId,
                            rating: rating === "thumbs-up" ? "POSITIVE" : "NEGATIVE",
                          })
                        }}
                        customControlsLeft={customControlsLeft}
                        customControlsRight={customControlsRight}
                        customControlsMenu={customControlsMenu}
                        emptyState={
                          <EmptyConversation
                            conversation={conversation}
                            onSelectOpeningMessage={(content) => {
                              createTestingMessage({
                                conversationId: conversation.id,
                                content,
                              })
                              setDisplayGenerating(true)
                            }}
                          />
                        }
                        replyMessageCopy={(kind) => t(`conversations.chat.replyToMessage.${kind}`)}
                        onSelectMessage={setSelectedMessage}
                        messageNotGeneratedByPermutCopy={t(
                          `conversations.chat.messageNotGeneratedByPermut`,
                        )}
                        messagePendingCopy={t("conversations.chat.messagePending")}
                        messagePendingTooltipCopy={t("conversations.chat.messagePendingTooltip")}
                        messageIsWhatsappTemplateCopy={t(
                          `conversations.chat.messageIsWhatsappTemplate`,
                        )}
                        messageIsAmphoraCopy={t(`conversations.chat.messageSentFromAmphora`)}
                        messageExternalSenderCopy={t(`conversations.chat.messageExternalSender`)}
                        messageExternalDeletedCopy={t(`conversations.chat.messageDeleted`)}
                        suppressLinks={!!customer.denylistedAt}
                        hiddenLinkCopy={t("conversations.chat.hiddenLink")}
                        noteItems={currentConversation?.notes}
                        noteComponent={Note}
                        noteInputComponent={NoteTextareaInput}
                        onEditNote={async (id, text) => {
                          await upsertNote({ id, text, conversationId: conversation.id })
                        }}
                        onDeleteNote={(id) =>
                          setDeletingNote({ id, conversationId: conversation.id })
                        }
                      />
                    </div>
                    <Separator className="hidden lg:block" orientation="vertical" />
                    <div className="hidden z-10 lg:flex flex-col gap-6 lg:px-4 overflow-y-auto pb-4 w-sm">
                      {extraInfo}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </div>
      {deletingNote && (
        <DeleteNoteDialog
          noteId={deletingNote.id}
          conversationId={deletingNote.conversationId}
          open={!!deletingNote}
          onClose={() => setDeletingNote(null)}
        />
      )}
    </div>
  )
}

function getUploadFileKey(file: File) {
  return [file.name, file.size, file.lastModified, file.type].join(":")
}
